import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePostHog } from 'posthog-js/react';
import { dmUnlockApi } from '../api/dmUnlock';
import {
  startBatch,
  updateProgress,
  setCompleted,
  setFailed,
  resetBatch,
  BatchKind,
  ActiveBatch,
} from '../store/slices/dmUnlockSlice';
import { BatchStatusResponse } from '../types/results.types';
import { useAppSelector } from './reduxHooks';

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'dm_unlock_active_batches'; // new key — avoids old single-object shape

export const saveActiveBatches = (batches: ActiveBatch[]) => {
  try {
    if (batches.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(batches));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // no-op
  }
};

const loadActiveBatches = (): ActiveBatch[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// ---------------------------------------------------------------------------
// Per-batch error/backoff state
// ---------------------------------------------------------------------------

interface BatchPollState {
  consecutiveErrors: number;
  consecutive404s: number;
  firstErrorTime: number | null;
  currentInterval: number;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_INTERVAL_MS = 16000;
const MAX_ERROR_DURATION_MS = 2 * 60 * 1000;
const MAX_404_RETRIES = 5;

// ---------------------------------------------------------------------------
// useDMUnlockManager — mounts once in App.tsx, drives all active batches
// ---------------------------------------------------------------------------

export const useDMUnlockManager = () => {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const activeBatches = useAppSelector((state) => state.dmUnlock.activeBatches);

  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;

  const activeBatchesRef = useRef(activeBatches);
  activeBatchesRef.current = activeBatches;

  const intervalRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const pollStateRefs = useRef<Map<string, BatchPollState>>(new Map());

  // Restore from localStorage on first mount
  useEffect(() => {
    const stored = loadActiveBatches();
    for (const batch of stored) {
      dispatch(startBatch({
        batchId: batch.batchId,
        kind: batch.kind,
        searchId: batch.searchId,
        personIds: batch.personIds,
        total: batch.total,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage whenever activeBatches changes
  useEffect(() => {
    saveActiveBatches(Object.values(activeBatches));
  }, [activeBatches]);

  // Reconcile polling intervals: start new ones, clear stale ones
  useEffect(() => {
    const activeIds = new Set(Object.keys(activeBatches));

    // Stop intervals for batches no longer active
    for (const [batchId, timer] of intervalRefs.current) {
      if (!activeIds.has(batchId)) {
        clearInterval(timer);
        intervalRefs.current.delete(batchId);
        pollStateRefs.current.delete(batchId);
      }
    }

    // Start intervals for newly added batches
    for (const batchId of activeIds) {
      if (intervalRefs.current.has(batchId)) continue;

      pollStateRefs.current.set(batchId, {
        consecutiveErrors: 0,
        consecutive404s: 0,
        firstErrorTime: null,
        currentInterval: POLL_INTERVAL_MS,
      });

      const poll = async () => {
        const ps = pollStateRefs.current.get(batchId);
        if (!ps) return;

        try {
          const response = await dmUnlockApi.getBatchStatus(batchId);
          ps.consecutiveErrors = 0;
          ps.consecutive404s = 0;
          ps.firstErrorTime = null;
          ps.currentInterval = POLL_INTERVAL_MS;

          dispatch(updateProgress(response));

          if (response.status === 'COMPLETED') {
            const batch = activeBatchesRef.current[batchId];
            posthogRef.current?.capture('enrich_completed', {
              found_count: response.succeeded,
              not_found_count: response.failed,
              total_count: response.total,
              context: batch?.kind ?? 'bulk',
              search_id: batch?.searchId ?? null,
            });
            clearInterval(intervalRefs.current.get(batchId)!);
            intervalRefs.current.delete(batchId);
            pollStateRefs.current.delete(batchId);
            dispatch(setCompleted(response));
          }
        } catch (error) {
          const status = (error as any)?.response?.status;

          if (status === 404) {
            ps.consecutive404s += 1;
            if (ps.consecutive404s >= MAX_404_RETRIES) {
              clearInterval(intervalRefs.current.get(batchId)!);
              intervalRefs.current.delete(batchId);
              pollStateRefs.current.delete(batchId);
              dispatch(setFailed(batchId));
            }
            return;
          }

          ps.consecutiveErrors += 1;
          if (ps.firstErrorTime === null) ps.firstErrorTime = Date.now();

          const elapsed = Date.now() - ps.firstErrorTime;
          if (elapsed >= MAX_ERROR_DURATION_MS) {
            clearInterval(intervalRefs.current.get(batchId)!);
            intervalRefs.current.delete(batchId);
            pollStateRefs.current.delete(batchId);
            dispatch(setFailed(batchId));
            return;
          }

          if (ps.consecutiveErrors >= 3) {
            ps.currentInterval = Math.min(ps.currentInterval * 2, MAX_POLL_INTERVAL_MS);
            clearInterval(intervalRefs.current.get(batchId)!);
            const newTimer = setInterval(poll, ps.currentInterval);
            intervalRefs.current.set(batchId, newTimer);
          }
        }
      };

      const timer = setInterval(poll, POLL_INTERVAL_MS);
      intervalRefs.current.set(batchId, timer);
    }

    return () => {
      // Do NOT clear intervals on every activeBatches change — only on unmount
    };
  }, [activeBatches, dispatch]);

  // Clear all intervals on unmount
  useEffect(() => {
    return () => {
      for (const timer of intervalRefs.current.values()) clearInterval(timer);
      intervalRefs.current.clear();
      pollStateRefs.current.clear();
    };
  }, []);

  return null;
};

// ---------------------------------------------------------------------------
// useDMUnlockPolling — per-subscriber hook (one per consumer component)
// ---------------------------------------------------------------------------

interface UseDMUnlockPollingOptions {
  onComplete?: () => void;
  onProgress?: (progress: number) => void;
  onError?: () => void;
  searchId?: number;
  personIds?: number[];
  kind?: BatchKind;
  attachBatchId?: string | null;
}

export const useDMUnlockPolling = (options?: UseDMUnlockPollingOptions) => {
  const dispatch = useDispatch();
  const { activeBatches, recentCompletions } = useAppSelector((state) => state.dmUnlock);

  const subscriberBatchIdRef = useRef<string | null>(null);
  const [subscriberBatchId, setSubscriberBatchId] = useState<string | null>(null);
  const lastHandledBatchIdRef = useRef<string | null>(null);
  const [completedBatchStatus, setCompletedBatchStatus] = useState<BatchStatusResponse | null>(null);
  const onCompleteRef = useRef(options?.onComplete);
  onCompleteRef.current = options?.onComplete;

  const startPolling = useCallback(
    (newBatchId: string, searchIdOverride?: number, personIdsOverride?: number[], totalOverride?: number) => {
      subscriberBatchIdRef.current = newBatchId;
      setSubscriberBatchId(newBatchId);
      lastHandledBatchIdRef.current = null;
      setCompletedBatchStatus(null);
      const sid = searchIdOverride ?? options?.searchId ?? null;
      const pids = personIdsOverride ?? options?.personIds ?? [];
      const kind = options?.kind ?? 'bulk';
      dispatch(startBatch({ batchId: newBatchId, kind, searchId: sid, personIds: pids, total: totalOverride }));
      // localStorage persistence is handled automatically by useDMUnlockManager's activeBatches effect
    },
    [dispatch, options?.searchId, options?.personIds, options?.kind],
  );

  useEffect(() => {
    if (!options?.attachBatchId) return;
    subscriberBatchIdRef.current = options.attachBatchId;
    setSubscriberBatchId(options.attachBatchId);
    lastHandledBatchIdRef.current = null;
    setCompletedBatchStatus(null);
  }, [options?.attachBatchId]);

  const stopPolling = useCallback(() => {
    if (subscriberBatchIdRef.current) {
      dispatch(resetBatch(subscriberBatchIdRef.current));
    }
    subscriberBatchIdRef.current = null;
    setSubscriberBatchId(null);
    lastHandledBatchIdRef.current = null;
  }, [dispatch]);

  // Fire onComplete when this subscriber's batch appears in recentCompletions
  useEffect(() => {
    if (!subscriberBatchId) return;
    const myCompletion = recentCompletions.find(
      (c) => c.batch_id === subscriberBatchId,
    );
    if (!myCompletion) return;
    if (lastHandledBatchIdRef.current === myCompletion.batch_id) return;

    lastHandledBatchIdRef.current = myCompletion.batch_id;
    setCompletedBatchStatus(myCompletion);
    onCompleteRef.current?.();
  }, [recentCompletions, subscriberBatchId, dispatch]);

  const myBatch = subscriberBatchId
    ? activeBatches[subscriberBatchId] ?? null
    : null;

  const isPolling = myBatch !== null;
  const batchStatus = myBatch?.batchStatus ?? null;
  const batchTotal = batchStatus?.total ?? myBatch?.total ?? myBatch?.personIds.length ?? 0;
  const progress = myBatch?.progress ?? 0;

  return {
    startPolling,
    stopPolling,
    batchStatus,
    completedBatchStatus,
    batchTotal,
    progress,
    isPolling,
    batchId: subscriberBatchId,
  };
};
